import { StackScreenProps } from "@react-navigation/stack";
import {
  CreateInvoiceArrivalRootScreenParamList,
  InvoiceArrivalRootParamList,
} from "../../../../types";
import { InvoiceArrivalDetailsForm } from "../../components";
import {
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
  View,
  Button,
  useFlashMessages,
  Divider,
} from "@towersystems/roam-common-native";
import { Colors } from "react-native-ui-lib";
import { InvoiceArrivalDetailsFormValues } from "../../components/InvoiceArrivalDetailsForm/types";
import { useFormContext } from "react-hook-form";

import { useMutation } from "@apollo/client";
import { MUTATION_CREATE_INVOICE_ARRIVAL } from "../../utils";
import { InvoiceArrival } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { LocationIdToName, SupplierIdToName } from "../../../../components";
import { useTranslation } from "../../../../utilities/i18n";
import { InvoiceFreightFormInput } from "../../components/InvoiceArrivalDetailsForm/components/InvoiceFreightFormInput";

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as any;

export type CreateInvoiceArrivalScreenProps = StackScreenProps<
  CreateInvoiceArrivalRootScreenParamList & InvoiceArrivalRootParamList,
  "CreateInvoiceArrivalScreen"
>;

export const CreateInvoiceArrivalScreen = ({
  navigation,
}: CreateInvoiceArrivalScreenProps) => {
  const { t } = useTranslation("invoice_arrival");
  const { watch, setError, formState, handleSubmit, trigger } =
    useFormContext<InvoiceArrivalDetailsFormValues>();

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;

  const watchedLocationId = watch("locationId");
  const watchedSupplierId = watch("supplierId");

  const [m] = useMutation<{ createInvoiceArrival: InvoiceArrival }>(
    MUTATION_CREATE_INVOICE_ARRIVAL
  );

  const flashMessagesContext = useFlashMessages();

  React.useEffect(() => {
    // to reset the form if the api returns error
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const handleOnSubmit = React.useCallback(
    async ({
      invoiceArrivalDate,
      invoiceDueDate,
      freightCost,
      ...input
    }: InvoiceArrivalDetailsFormValues) => {
      try {
        const result = await m({
          variables: {
            input: {
              ...input,
              dueDate: invoiceDueDate,
              arrivalDate: invoiceArrivalDate,
              freightCost: freightCost ? parseFloat(freightCost) : undefined,
            },
          },
        });

        if (result.data && !result.errors) {
          navigation.reset({
            index: 0,
            routes: [
              { name: "InvoiceArrivalsLandingScreen" },
              {
                name: "EditInvoiceArrivalRootScreen",
                params: {
                  invoiceArrivalId: result.data.createInvoiceArrival.id,
                },
              },
            ],
          });
        } else {
          throw new Error(t("common:error"));
        }
        // reset the navigation stack
        // and route to the edit screen
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
        setError("root.serverError", {
          type: "400",
          message: t("common:error"),
        });
      }
    },
    []
  );

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.create")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page footer={footerMarkup}>
      <Stack vertical>
        <Card title={t("card.delivery.title")}>
          <FlatActionList
            items={[
              {
                key: "location",
                title: t("form.location.label"),
                subTitle: t("form.location.description"),
                onPress: () => navigation.navigate("SelectLocationModalScreen"),
                componentRight: () =>
                  watchedLocationId ? (
                    <LocationIdToName
                      locationId={watchedLocationId}
                      {...textButtonProps}
                    />
                  ) : (
                    <Text {...textButtonProps}>Select Location</Text>
                  ),
              },
              {
                key: "supplier",
                title: t("form.supplier.label"),
                subTitle: t("form.location.description"),
                onPress: () => navigation.navigate("SelectSupplierModalScreen"),
                componentRight: () =>
                  watchedSupplierId ? (
                    <SupplierIdToName
                      supplierId={watchedSupplierId}
                      {...textButtonProps}
                    />
                  ) : (
                    <Text {...textButtonProps}>Select Supplier</Text>
                  ),
              },
            ]}
          />
          <Divider />
          <InvoiceFreightFormInput />
        </Card>
        <Divider />
        <View>
          <InvoiceArrivalDetailsForm />
        </View>
      </Stack>
    </Page>
  );
};
